import CircularProgressBar from '@mui/material/CircularProgress'
import { PlayIcon } from 'components/Icons'
import { Facebook, FacebookVideoPlayer, FacebookVideoPlayerEventHandler, Video } from 'interfaces'
import { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Subject } from 'rxjs'
import { debounceTime, filter, takeUntil } from 'rxjs/operators'
import { openContentPreviewWindow } from 'services/contentPreview'
import { StoreDispatch } from 'store/state'
import styles from './FBVideoPlayer.pcss'

declare const FB: Facebook
export const FB_PARSE_VIDEO_CLASSNAME = 'fb-video'
export const FB_NO_PARSE_CLASSNAME = 'fb-xfbml-parse-ignore'
const THROTTLE_HOVER_PLAY_MS = 500
const LOADER_SIZE = 48

export interface FBVideoPlayerOwnProps {
  videoUrl: string
  videoId: string
  parseOnMount?: boolean
  autoplay?: boolean
  togglePlayOnHover?: boolean
  contentItem?: Video
  thumbnailUrl?: string
  width?: number
  windowMode?: boolean
  withBackground?: boolean
  className?: string
  videoContainerClassName?: string
  imageClassName?: string

  onVideoParsed?: () => void
}

interface ConnectedFBVideoPlayerProps {
  openInWindow: (content: Video) => void
}

export type FBVideoPlayerProps = FBVideoPlayerOwnProps & ConnectedFBVideoPlayerProps

export const FBVideoPlayer = ({
  videoUrl,
  videoId,
  parseOnMount,
  autoplay,
  togglePlayOnHover,
  contentItem,
  thumbnailUrl,
  width,
  windowMode,
  withBackground,
  className,
  videoContainerClassName,
  imageClassName,
  onVideoParsed,
  openInWindow
}: FBVideoPlayerProps) => {
  const [state, setState] = useState({
    active: Boolean(windowMode),
    loading: false,
    playing: false
  })

  const elementRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<FacebookVideoPlayer | null>(null)
  const unmount$ = useRef(new Subject<boolean>())
  const playing$ = useRef(new Subject<boolean>())
  const isMouseOver$ = useRef(new Subject<boolean>())
  const eventHandlersRef = useRef<{
    startedPlaying?: FacebookVideoPlayerEventHandler
    finishedPlaying?: FacebookVideoPlayerEventHandler
    pausedPlaying?: FacebookVideoPlayerEventHandler
  }>({})

  const handleVideoParsed = useCallback((msg: any) => {
    if (msg.type === 'video' && msg.id === videoId) {
      playerRef.current = msg.instance as FacebookVideoPlayer

      setState(prev => ({ ...prev, loading: false }))
      
      eventHandlersRef.current.startedPlaying = playerRef.current.subscribe('startedPlaying', () => {
        setState(prev => ({ ...prev, loading: false, playing: true }))
      })

      eventHandlersRef.current.finishedPlaying = playerRef.current.subscribe('finishedPlaying', () => {
        setState(prev => ({ ...prev, playing: false }))
      })

      eventHandlersRef.current.pausedPlaying = playerRef.current.subscribe('paused', () => {
        setState(prev => ({ ...prev, playing: false }))
      })

      if (autoplay) {
        playing$.current.next(true)
      }

      if (onVideoParsed) {
        onVideoParsed()
      }
    }
  }, [videoId, autoplay, onVideoParsed])

  const parseVideoElement = useCallback(() => {
    if (!playerRef.current && elementRef.current) {
      setState(prev => ({ ...prev, loading: true }))
      FB.XFBML.parse(elementRef.current)
    }
  }, [])

  const play = useCallback(() => {
    setState(prev => ({ ...prev, active: true }))
    if (playerRef.current) {
      playing$.current.next(true)
    } else {
      parseVideoElement()
    }
  }, [parseVideoElement])

  const pause = useCallback(() => {
    setState(prev => ({ ...prev, active: false, loading: false }))
    playing$.current.next(false)
  }, [])

  const togglePlay = useCallback(() => {
    setState(prev => {
      const newActive = !prev.playing
      if (newActive) {
        if (playerRef.current) {
          playing$.current.next(true)
        } else {
          parseVideoElement()
        }
      } else {
        playing$.current.next(false)
      }
      return { ...prev, active: newActive }
    })
  }, [parseVideoElement])

  const handleMouseEnter = useCallback(() => {
    isMouseOver$.current.next(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    isMouseOver$.current.next(false)
  }, [])

  const handleOverlayClick = useCallback(() => {
    if (contentItem) {
      openInWindow(contentItem)
    }
  }, [contentItem, openInWindow])

  // Initialize subscriptions
  useEffect(() => {
    unmount$.current = new Subject()
    isMouseOver$.current = new Subject()
    playing$.current = new Subject()

    const eventHandlers = eventHandlersRef.current

    // Playing subscription
    const playingSubscription = playing$
      .current.pipe(takeUntil(unmount$.current))
      .subscribe((willPlay: boolean) => {
        if (!playerRef.current) {
          return
        }

        if (state.active && willPlay) {
          playerRef.current.mute()
          playerRef.current.play()
          if (windowMode) {
            playerRef.current.unmute()
          }
          return
        }

        if (!state.active || !willPlay) {
          playerRef.current.pause()
        }
      })

    // Mouse over subscription
    const mouseOverSubscription = isMouseOver$
      .current.pipe(
        takeUntil(unmount$.current),
        debounceTime(THROTTLE_HOVER_PLAY_MS),
        filter(() => Boolean(togglePlayOnHover))
      )
      .subscribe((isMouseOver: boolean) => {
        if (isMouseOver) {
          play()
        } else {
          pause()
        }
      })

    FB.Event.subscribe('xfbml.ready', handleVideoParsed)

    if (parseOnMount) {
      parseVideoElement()
    }

    return () => {
      FB.Event.unsubscribe('xfbml.ready', handleVideoParsed)
      playingSubscription.unsubscribe()
      mouseOverSubscription.unsubscribe()
      unmount$.current.next(true)

      if (eventHandlers.startedPlaying) {
        eventHandlers.startedPlaying.release()
      }

      if (eventHandlers.finishedPlaying) {
        eventHandlers.finishedPlaying.release()
      }

      if (eventHandlers.pausedPlaying) {
        eventHandlers.pausedPlaying.release()
      }
    }
  }, [handleVideoParsed, parseOnMount, parseVideoElement, windowMode, togglePlayOnHover, play, pause, state.active])

  const displayWidth = width || 'auto'
  const isParsed = Boolean(playerRef.current)
  const isWindowMode = Boolean(windowMode)
  const backgroundStyles = { backgroundImage: `url('${thumbnailUrl}')` }
  const classFixed = !isParsed ? styles.fixed : ''

  return (
    <div
      data-testid="fb-video-player"
      ref={elementRef}
      className={`${styles.container} ${className || ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        id={videoId}
        className={`${FB_PARSE_VIDEO_CLASSNAME} ${styles['fb-box']} ${classFixed} ${videoContainerClassName || ''}`}
        data-href={videoUrl}
        data-width={displayWidth}
        data-allowfullscreen={isWindowMode}
        data-autoplay={Boolean(autoplay)}
        data-show-text={isWindowMode}
        data-show-captions={isWindowMode}
        data-lazy
      >
      </div>
      {!isWindowMode && contentItem && (
        <div className={styles.overlay} onClick={handleOverlayClick}></div>
      )}
      {withBackground && <div className={styles.background} style={backgroundStyles}></div>}
      {(!playerRef.current || !state.playing) && !isWindowMode && !isParsed && (
        <div className={styles.thumbnail}>
          {thumbnailUrl && (
            <img src={thumbnailUrl} className={`${imageClassName || ''} ${styles['thumb-img']}`} />
          )}
          {state.loading && (
            <div className={styles.loader}>
              <CircularProgressBar color="primary" size={LOADER_SIZE} className={styles['icon-loader']} />
            </div>
          )}
        </div>
      )}
      {!isWindowMode && !state.loading && !isParsed && (!playerRef.current || !state.playing) && (
        <PlayIcon center className={styles['icon-play']} onClick={togglePlay} />
      )}
    </div>
  )
}

function mapDispatchToProps(dispatch: StoreDispatch) {
  return {
    openInWindow: (content: Video) => dispatch(openContentPreviewWindow(content))
  }
}

export default connect<{}, ConnectedFBVideoPlayerProps, FBVideoPlayerOwnProps>(undefined, mapDispatchToProps)(FBVideoPlayer)
